import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import TypographyRebrand from 'src/components/atoms/TypographyRebrand/TypographyRebrand'
import { FeaturedProjectsRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'
import { CmsCtaButtonRebrand } from 'src/components/molecules/CmsCtaButtonRebrand'
import { ProjectCardRebrand } from 'src/components/molecules/ProjectCardRebrand'

import { GetProjectListingQuery } from 'src/config/generated/graphql'
import { viewProjectPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { ProjectFeaturedOn } from 'src/types/project'
import { trpc } from 'src/utils/trpc'

type ProjectFromListing = GetProjectListingQuery['Projects'][number]

type BackgroundStyle =
  | { kind: 'color'; className: string }
  | { kind: 'pattern'; src: string; overlayClassName?: string }

type BackgroundKey = 'lavenderPale' | 'limeFresh' | 'pattern1'

const BACKGROUND_STYLES: Record<BackgroundKey, BackgroundStyle> = {
  lavenderPale: { kind: 'color', className: 'bg-lavenderPale' },
  limeFresh: { kind: 'color', className: 'bg-limeFresh' },
  pattern1: {
    kind: 'pattern',
    src: '/img/hero/patterns/patternGreenMarble.png',
    overlayClassName: 'bg-limeBright/50'
  }
}

const CAROUSEL_SETTINGS = {
  arrows: false,
  dots: true,
  infinite: false,
  speed: 400,
  slidesToShow: 1,
  slidesToScroll: 1,
  swipe: true,
  swipeToSlide: true
}

const buildCardProps = (project: ProjectFromListing) => ({
  key: project.Id,
  // Assumes project.Hero is set. If null, this URL becomes `${DOMAIN}/null` and the <img> 404s.
  imageUrl: `${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${project.Hero}`,
  name: project.Name,
  location:
    [project.District?.Name, project.Region?.Name].filter(Boolean).join(', ') ||
    undefined,
  treeCount:
    project.Applications_aggregate.aggregate?.sum?.Approved ?? undefined,
  href: project.Uri
    ? viewProjectPath({ routeParams: { uri: project.Uri } })
    : undefined
})

// Only the presentational fields are needed, so this can be rendered from the
// Sanity dispatcher ({...data}) or standalone (e.g. the legacy NativeTrees page).
type FeaturedProjectsSectionRebrandProps = Pick<
  FeaturedProjectsRebrandResult,
  'title' | 'ctas' | 'backgroundColor'
>

const FeaturedProjectsSectionRebrand = ({
  title,
  ctas,
  backgroundColor
}: FeaturedProjectsSectionRebrandProps) => {
  const { data: projectListings } = trpc.useQuery(
    [
      'projects.listProjects',
      {
        purposes: [],
        regionId: undefined,
        limit: 3,
        offset: 0,
        orderBy: 'CreatedOn',
        featuredOn: [ProjectFeaturedOn.Home, ProjectFeaturedOn.All]
      }
    ],
    { refetchOnWindowFocus: false }
  )

  const projects = projectListings?.Projects ?? []

  if (projects.length === 0) return null

  const cardProps = projects.map(buildCardProps)

  const backgroundStyle: BackgroundStyle =
    (backgroundColor && BACKGROUND_STYLES[backgroundColor as BackgroundKey]) ||
    BACKGROUND_STYLES.lavenderPale

  return (
    <section
      className={`relative overflow-hidden ${
        backgroundStyle.kind === 'color' ? backgroundStyle.className : ''
      }`}
    >
      {backgroundStyle.kind === 'pattern' && (
        <>
          <div
            aria-hidden
            className='absolute inset-0 pointer-events-none bg-cover bg-center'
            style={{ backgroundImage: `url(${backgroundStyle.src})` }}
          />
          {backgroundStyle.overlayClassName && (
            <div
              aria-hidden
              className={`absolute inset-0 pointer-events-none ${backgroundStyle.overlayClassName}`}
            />
          )}
        </>
      )}
      <div className='relative py-10 lg:py-24'>
        <div className='container mx-auto px-4 lg:px-20'>
          <div className='flex items-center justify-between mb-4'>
            {title && (
              <TypographyRebrand variant='h4' color='forestDeep'>
                {title}
              </TypographyRebrand>
            )}
            {ctas && ctas.length > 0 && (
              <div className='hidden lg:flex lg:flex-row lg:gap-2'>
                {ctas.map((cta, idx) => (
                  <CmsCtaButtonRebrand
                    key={`cta_${idx}`}
                    {...cta}
                    colorVariant='forest'
                  />
                ))}
              </div>
            )}
          </div>

          <div className='hidden lg:grid lg:grid-cols-3 lg:gap-4'>
            {cardProps.map(({ key, ...props }) => (
              <ProjectCardRebrand key={key} {...props} />
            ))}
          </div>

          <div className='lg:hidden'>
            <Slider {...CAROUSEL_SETTINGS}>
              {cardProps.map(({ key, ...props }) => (
                <div key={key} className='px-2'>
                  <ProjectCardRebrand {...props} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </section>
  )
}

export default FeaturedProjectsSectionRebrand
